/**
 * RASHII DEADLINE ADMIN CARDS
 * ============================
 * Admin Control Layer for Contract-Sourced Deadlines
 * 
 * Features:
 * - Deadline cards grouped by deal
 * - Status badges: DRAFT / ACTIVE / COMPLETED / OVERDUE
 * - Responsibility badges: Buyer / Seller / Bank / Both
 * - Source reference (Form F ID)
 * - Reminder schedule visibility
 * - Condition link (if overdue)
 * - Admin override buttons: Amend, Complete, Cancel
 */

import React, { useState, useEffect } from 'react';
import {
  Clock, CheckCircle2, XCircle, AlertTriangle, Calendar,
  Edit3, Trash2, ChevronDown, ChevronUp, FileText, Link2,
  Bell, User, Users, Building2, Landmark, RefreshCw,
  AlertCircle, Timer, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import api from '@/services/api';

// ============ CONSTANTS ============

const ACTIVATION_STATUS_CONFIG = {
  DRAFT: {
    color: 'bg-gray-600/20 text-gray-400 border-gray-600/30',
    icon: Clock,
    label: 'Draft',
    description: 'Pending signature'
  },
  ACTIVE: {
    color: 'bg-blue-600/20 text-blue-400 border-blue-600/30',
    icon: Timer,
    label: 'Active',
    description: 'Enforced'
  },
  COMPLETED: {
    color: 'bg-green-600/20 text-green-400 border-green-600/30',
    icon: CheckCircle2,
    label: 'Completed',
    description: 'Milestone met'
  },
  OVERDUE: {
    color: 'bg-red-600/20 text-red-400 border-red-600/30',
    icon: AlertTriangle,
    label: 'Overdue',
    description: 'Deadline passed'
  },
  AMENDED: {
    color: 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30',
    icon: Edit3,
    label: 'Amended',
    description: 'Date changed'
  },
  CANCELLED: {
    color: 'bg-gray-600/20 text-gray-500 border-gray-600/30',
    icon: XCircle,
    label: 'Cancelled',
    description: 'No longer tracked'
  }
};

const RESPONSIBILITY_CONFIG = {
  BUYER: { color: 'bg-purple-600/20 text-purple-400', icon: User, label: 'Buyer' },
  SELLER: { color: 'bg-primary/20 text-primary', icon: User, label: 'Seller' },
  BOTH: { color: 'bg-cyan-600/20 text-cyan-400', icon: Users, label: 'Both Parties' },
  BANK: { color: 'bg-blue-600/20 text-blue-400', icon: Landmark, label: 'Bank' },
  DEVELOPER: { color: 'bg-amber-600/20 text-amber-400', icon: Building2, label: 'Developer' },
  AGENT: { color: 'bg-green-600/20 text-green-400', icon: User, label: 'Agent' }
};

const SEVERITY_CONFIG = {
  NORMAL: { color: 'text-gray-400', label: 'Normal' },
  URGENT: { color: 'text-yellow-400', label: 'Urgent' },
  CRITICAL: { color: 'text-orange-400', label: 'Critical' },
  OVERDUE: { color: 'text-red-400', label: 'Overdue' },
  PENDING: { color: 'text-gray-500', label: 'Pending' }
};

const DEADLINE_TYPE_LABELS = {
  COMPLETION_DATE: 'Deal Completion',
  TRUSTEE_APPOINTMENT: 'Trustee Appointment',
  NOC_SUBMISSION: 'NOC Submission',
  DEPOSIT_PAYMENT: 'Deposit Payment',
  FINAL_PAYMENT: 'Final Payment',
  MORTGAGE_APPROVAL: 'Mortgage Approval',
  DOCUMENT_SUBMISSION: 'Document Pack',
  HANDOVER: 'Property Handover',
  FORM_F_SIGNING: 'Form F Signing',
  INSTALLMENT_DUE: 'Installment Due'
};

// ============ HELPER FUNCTIONS ============

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-AE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleString('en-AE', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const deadline = new Date(dateStr);
  const now = new Date();
  const diffTime = deadline - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

const getDaysLabel = (days) => {
  if (days === null) return '';
  if (days < 0) return `${Math.abs(days)} days overdue`;
  if (days === 0) return 'Due today';
  if (days === 1) return '1 day left';
  return `${days} days left`;
};

// ============ AMEND DEADLINE DIALOG ============

const AmendDeadlineDialog = ({ deadline, open, onOpenChange, onAmend }) => {
  const [newDate, setNewDate] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (deadline?.deadline_date) {
      const date = new Date(deadline.deadline_date);
      setNewDate(date.toISOString().split('T')[0]);
    }
  }, [deadline]);

  const handleAmend = async () => {
    if (!newDate || !reason.trim()) {
      toast.error('Please provide new date and reason');
      return;
    }

    setLoading(true);
    try {
      await api.put(`/contracts/deadline/${deadline.deadline_id}/date`, {
        new_date: newDate,
        reason: reason.trim()
      });
      toast.success('Deadline amended successfully');
      onAmend?.();
      onOpenChange(false);
      setReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to amend deadline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-primary" />
            Amend Deadline
          </DialogTitle>
          <DialogDescription>
            {deadline?.label || DEADLINE_TYPE_LABELS[deadline?.deadline_type] || 'Deadline'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 rounded bg-secondary/30 text-sm">
            <p className="text-muted-foreground">Current Date:</p>
            <p className="font-medium">{formatDate(deadline?.deadline_date)}</p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">New Date</Label>
            <Input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="bg-transparent border-white/20 mt-1"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason for Amendment</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Buyer requested extension due to mortgage processing delay"
              className="bg-transparent border-white/20 mt-1 min-h-[80px]"
            />
          </div>

          {deadline?.amendment_count > 0 && (
            <div className="p-2 rounded bg-yellow-600/10 border border-yellow-600/30 text-xs">
              <AlertTriangle className="w-3 h-3 inline mr-1 text-yellow-400" />
              This deadline has been amended {deadline.amendment_count} time(s)
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleAmend} disabled={loading} className="btn-gold-metallic">
            {loading ? 'Saving...' : 'Amend Deadline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ COMPLETE DEADLINE DIALOG ============

const CompleteDeadlineDialog = ({ deadline, open, onOpenChange, onComplete }) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleComplete = async () => {
    setLoading(true);
    try {
      await api.post(`/contracts/deadline/${deadline.deadline_id}/complete`, {
        notes: notes.trim() || null
      });
      toast.success('Deadline marked as completed');
      onComplete?.();
      onOpenChange(false);
      setNotes('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete deadline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5 text-green-400" />
            Mark Deadline Complete
          </DialogTitle>
          <DialogDescription>
            {deadline?.label || DEADLINE_TYPE_LABELS[deadline?.deadline_type] || 'Deadline'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 rounded bg-green-600/10 border border-green-600/30">
            <p className="text-sm">Due: <span className="font-medium">{formatDate(deadline?.deadline_date)}</span></p>
            <p className="text-xs text-muted-foreground mt-1">
              Marking complete will stop reminders and update governance status.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Completion Notes (Optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g., Deposit received via bank transfer on 15 Mar"
              className="bg-transparent border-white/20 mt-1 min-h-[60px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleComplete} disabled={loading} className="bg-green-600 hover:bg-green-700">
            {loading ? 'Saving...' : 'Mark Complete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ CANCEL DEADLINE DIALOG ============

const CancelDeadlineDialog = ({ deadline, open, onOpenChange, onCancel }) => {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCancel = async () => {
    if (!reason.trim()) {
      toast.error('Please provide a reason');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/contracts/deadline/${deadline.deadline_id}/cancel`, {
        reason: reason.trim()
      });
      toast.success('Deadline cancelled');
      onCancel?.();
      onOpenChange(false);
      setReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel deadline');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-card max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-400" />
            Cancel Deadline
          </DialogTitle>
          <DialogDescription>
            {deadline?.label || DEADLINE_TYPE_LABELS[deadline?.deadline_type] || 'Deadline'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="p-3 rounded bg-red-600/10 border border-red-600/30">
            <p className="text-sm text-red-400">This action cannot be undone.</p>
            <p className="text-xs text-muted-foreground mt-1">
              Cancelling will stop all reminders and remove from governance tracking.
            </p>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Reason for Cancellation</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Deal cancelled, deadline no longer applicable"
              className="bg-transparent border-white/20 mt-1 min-h-[80px]"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Keep Deadline</Button>
          <Button onClick={handleCancel} disabled={loading} variant="destructive">
            {loading ? 'Cancelling...' : 'Cancel Deadline'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ SINGLE DEADLINE CARD ============

const DeadlineCard = ({ deadline, onRefresh }) => {
  const [amendOpen, setAmendOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const status = ACTIVATION_STATUS_CONFIG[deadline.activation_status] || ACTIVATION_STATUS_CONFIG.DRAFT;
  const StatusIcon = status.icon;
  const responsibility = RESPONSIBILITY_CONFIG[deadline.responsibility] || RESPONSIBILITY_CONFIG.BOTH;
  const ResponsibilityIcon = responsibility.icon;
  const severity = SEVERITY_CONFIG[deadline.current_severity] || SEVERITY_CONFIG.NORMAL;
  const daysUntil = getDaysUntil(deadline.deadline_date);
  const isOverdue = daysUntil !== null && daysUntil < 0;
  const isUrgent = daysUntil !== null && daysUntil <= 3 && daysUntil >= 0;

  return (
    <>
      <Card 
        className={`glass-card border transition-all ${
          isOverdue ? 'border-red-600/50' : 
          isUrgent ? 'border-yellow-600/50' : 
          'border-white/10'
        }`}
        data-testid={`deadline-card-${deadline.deadline_id}`}
      >
        <CardContent className="p-4">
          {/* Header Row */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h4 className="font-medium text-sm">
                  {deadline.label || DEADLINE_TYPE_LABELS[deadline.deadline_type] || deadline.deadline_type}
                </h4>
                <Badge className={`${status.color} text-xs`}>
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
              </div>
              
              <p className="text-xs text-muted-foreground mt-1">
                {deadline.description}
              </p>
            </div>

            {/* Date & Days */}
            <div className="text-right">
              <p className="text-sm font-medium">{formatDate(deadline.deadline_date)}</p>
              {daysUntil !== null && (
                <p className={`text-xs ${severity.color}`}>
                  {getDaysLabel(daysUntil)}
                </p>
              )}
            </div>
          </div>

          {/* Info Row */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {/* Responsibility */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Badge className={`${responsibility.color} text-xs`}>
                    <ResponsibilityIcon className="w-3 h-3 mr-1" />
                    {responsibility.label}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>Responsible Party</TooltipContent>
              </Tooltip>
            </TooltipProvider>

            {/* Reminders */}
            {deadline.reminders_enabled && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge className="bg-blue-600/10 text-blue-400 text-xs">
                      <Bell className="w-3 h-3 mr-1" />
                      Reminders On
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>T-7, T-1, Overdue alerts</p>
                    {deadline.reminder_7_day_sent && <p className="text-green-400">✓ 7-day sent</p>}
                    {deadline.reminder_1_day_sent && <p className="text-green-400">✓ 1-day sent</p>}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Source Contract */}
            {deadline.source_contract_id && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Badge variant="outline" className="text-xs border-white/20">
                      <FileText className="w-3 h-3 mr-1" />
                      {deadline.source_contract_id.slice(0, 12)}...
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Source: {deadline.source_contract_type || 'Form F'}</p>
                    <p className="text-xs">{deadline.source_contract_id}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Condition Link */}
            {deadline.condition_created && deadline.condition_id && (
              <Badge className="bg-red-600/20 text-red-400 text-xs">
                <Link2 className="w-3 h-3 mr-1" />
                Condition Created
              </Badge>
            )}

            {/* Amendment Count */}
            {deadline.amendment_count > 0 && (
              <Badge className="bg-yellow-600/10 text-yellow-400 text-xs">
                <Edit3 className="w-3 h-3 mr-1" />
                Amended ×{deadline.amendment_count}
              </Badge>
            )}
          </div>

          {/* Expanded Details */}
          <Collapsible open={expanded} onOpenChange={setExpanded}>
            <CollapsibleContent className="mt-3 pt-3 border-t border-white/10">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p>{formatDateTime(deadline.created_at)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Last Updated</p>
                  <p>{formatDateTime(deadline.updated_at)}</p>
                </div>
                {deadline.original_date && deadline.original_date !== deadline.deadline_date && (
                  <div>
                    <p className="text-muted-foreground">Original Date</p>
                    <p>{formatDate(deadline.original_date)}</p>
                  </div>
                )}
                {deadline.activated_at && (
                  <div>
                    <p className="text-muted-foreground">Activated</p>
                    <p>{formatDateTime(deadline.activated_at)}</p>
                  </div>
                )}
                {deadline.completed_at && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">Completed</p>
                    <p className="text-green-400">{formatDateTime(deadline.completed_at)} by {deadline.completed_by}</p>
                    {deadline.completion_notes && (
                      <p className="mt-1 text-muted-foreground italic">"{deadline.completion_notes}"</p>
                    )}
                  </div>
                )}
                {deadline.source_clause_ref && (
                  <div>
                    <p className="text-muted-foreground">Clause Ref</p>
                    <p>{deadline.source_clause_ref}</p>
                  </div>
                )}
              </div>
            </CollapsibleContent>

            {/* Action Buttons */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/10">
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="text-xs">
                  {expanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                  {expanded ? 'Less' : 'More'}
                </Button>
              </CollapsibleTrigger>

              {/* Admin Actions - only for non-completed/cancelled */}
              {!['COMPLETED', 'CANCELLED'].includes(deadline.activation_status) && (
                <div className="flex gap-2">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs"
                          onClick={() => setAmendOpen(true)}
                        >
                          <Edit3 className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Amend Date</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs text-green-400 hover:text-green-300"
                          onClick={() => setCompleteOpen(true)}
                        >
                          <CheckCircle2 className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Mark Complete</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>

                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-xs text-red-400 hover:text-red-300"
                          onClick={() => setCancelOpen(true)}
                        >
                          <XCircle className="w-3 h-3" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Cancel Deadline</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
            </div>
          </Collapsible>
        </CardContent>
      </Card>

      {/* Dialogs */}
      <AmendDeadlineDialog 
        deadline={deadline} 
        open={amendOpen} 
        onOpenChange={setAmendOpen}
        onAmend={onRefresh}
      />
      <CompleteDeadlineDialog 
        deadline={deadline} 
        open={completeOpen} 
        onOpenChange={setCompleteOpen}
        onComplete={onRefresh}
      />
      <CancelDeadlineDialog 
        deadline={deadline} 
        open={cancelOpen} 
        onOpenChange={setCancelOpen}
        onCancel={onRefresh}
      />
    </>
  );
};

// ============ DEADLINE SUMMARY STATS ============

const DeadlineSummaryStats = ({ deadlines }) => {
  const stats = {
    total: deadlines.length,
    draft: deadlines.filter(d => d.activation_status === 'DRAFT').length,
    active: deadlines.filter(d => d.activation_status === 'ACTIVE').length,
    completed: deadlines.filter(d => d.activation_status === 'COMPLETED').length,
    overdue: deadlines.filter(d => d.activation_status === 'OVERDUE').length,
    cancelled: deadlines.filter(d => d.activation_status === 'CANCELLED').length
  };

  const overdueCount = deadlines.filter(d => {
    if (d.activation_status !== 'ACTIVE') return false;
    const days = getDaysUntil(d.deadline_date);
    return days !== null && days < 0;
  }).length;

  const urgentCount = deadlines.filter(d => {
    if (d.activation_status !== 'ACTIVE') return false;
    const days = getDaysUntil(d.deadline_date);
    return days !== null && days >= 0 && days <= 3;
  }).length;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
      <div className="p-3 rounded bg-secondary/30 text-center">
        <p className="text-2xl font-bold">{stats.total}</p>
        <p className="text-xs text-muted-foreground">Total</p>
      </div>
      <div className="p-3 rounded bg-blue-600/10 text-center">
        <p className="text-2xl font-bold text-blue-400">{stats.active}</p>
        <p className="text-xs text-muted-foreground">Active</p>
      </div>
      <div className="p-3 rounded bg-green-600/10 text-center">
        <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
        <p className="text-xs text-muted-foreground">Completed</p>
      </div>
      <div className="p-3 rounded bg-gray-600/10 text-center">
        <p className="text-2xl font-bold text-gray-400">{stats.draft}</p>
        <p className="text-xs text-muted-foreground">Draft</p>
      </div>
      {overdueCount > 0 && (
        <div className="p-3 rounded bg-red-600/10 text-center">
          <p className="text-2xl font-bold text-red-400">{overdueCount}</p>
          <p className="text-xs text-muted-foreground">Overdue</p>
        </div>
      )}
      {urgentCount > 0 && (
        <div className="p-3 rounded bg-yellow-600/10 text-center">
          <p className="text-2xl font-bold text-yellow-400">{urgentCount}</p>
          <p className="text-xs text-muted-foreground">Urgent (&lt;3d)</p>
        </div>
      )}
    </div>
  );
};

// ============ MAIN DEADLINE ADMIN PANEL ============

const DeadlineAdminPanel = ({ dealId, contractId, showStats = true }) => {
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, active, overdue, completed

  useEffect(() => {
    if (dealId || contractId) {
      fetchDeadlines();
    }
  }, [dealId, contractId]);

  const fetchDeadlines = async () => {
    setLoading(true);
    try {
      let response;
      if (dealId) {
        response = await api.get(`/contracts/deal/${dealId}/deadlines`);
      } else if (contractId) {
        response = await api.get(`/contracts/${contractId}/deadlines`);
      }
      setDeadlines(response.data.deadlines || []);
    } catch (error) {
      console.error('Error fetching deadlines:', error);
      toast.error('Failed to load deadlines');
    } finally {
      setLoading(false);
    }
  };

  const filteredDeadlines = deadlines.filter(d => {
    if (filter === 'all') return true;
    if (filter === 'active') return d.activation_status === 'ACTIVE';
    if (filter === 'overdue') {
      const days = getDaysUntil(d.deadline_date);
      return d.activation_status === 'ACTIVE' && days !== null && days < 0;
    }
    if (filter === 'completed') return d.activation_status === 'COMPLETED';
    if (filter === 'draft') return d.activation_status === 'DRAFT';
    return true;
  });

  // Sort: overdue first, then by date
  const sortedDeadlines = [...filteredDeadlines].sort((a, b) => {
    const daysA = getDaysUntil(a.deadline_date) ?? 999;
    const daysB = getDaysUntil(b.deadline_date) ?? 999;
    
    // Overdue first
    if (daysA < 0 && daysB >= 0) return -1;
    if (daysB < 0 && daysA >= 0) return 1;
    
    // Then by date
    return daysA - daysB;
  });

  if (!dealId && !contractId) {
    return (
      <Card className="glass-card border-white/10">
        <CardContent className="py-8 text-center">
          <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Select a deal to view deadlines</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="deadline-admin-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Clock className="w-5 h-5 text-primary" />
            Governance Deadlines
          </h3>
          <p className="text-xs text-muted-foreground">
            {dealId ? `Deal: ${dealId}` : `Contract: ${contractId}`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchDeadlines} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      {showStats && deadlines.length > 0 && (
        <DeadlineSummaryStats deadlines={deadlines} />
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'active', 'overdue', 'completed', 'draft'].map(f => (
          <Button
            key={f}
            variant={filter === f ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f)}
            className={filter === f ? 'btn-gold-metallic' : ''}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Deadline Cards */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-6 h-6 mx-auto animate-spin text-primary" />
          <p className="text-sm text-muted-foreground mt-2">Loading deadlines...</p>
        </div>
      ) : sortedDeadlines.length === 0 ? (
        <Card className="glass-card border-white/10 border-dashed">
          <CardContent className="py-8 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {filter === 'all' ? 'No deadlines found' : `No ${filter} deadlines`}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {sortedDeadlines.map(deadline => (
            <DeadlineCard 
              key={deadline.deadline_id} 
              deadline={deadline} 
              onRefresh={fetchDeadlines}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default DeadlineAdminPanel;
export { DeadlineCard, DeadlineSummaryStats };
